/**
 * The people-discovery radius slider's geometry, as pure functions.
 *
 * The product asks for three labelled anchors — Close by (500 m) on the left,
 * Nearby (5 km) in the middle, Local area (10 km) on the right — which a
 * linear track cannot give on its own. So the track is piecewise-linear in two
 * halves: the left spans 500–5000 m in 50 m notches, the right 5000–10000 m in
 * 100 m notches. Each half therefore covers a comparable distance, and the
 * default lands exactly on the middle notch rather than at one end.
 *
 * No imports on purpose: this must stay loadable by the root `node --test`
 * suites, which cannot load anything that pulls `react-native`.
 *
 * The anchor values mirror the server's `common/nearby-radius.ts` and the
 * public config's `location` block. The server clamps on write, so a drifted
 * copy here can mislabel the track but never widen anyone's visibility. The
 * reasoning behind each bound lives on the server copy — it is the one that
 * enforces them.
 */

export const NEARBY_RADIUS_MIN_M = 500;
export const NEARBY_RADIUS_MID_M = 5000;
export const NEARBY_RADIUS_MAX_M = 10000;

/**
 * The default is the MIDDLE anchor, not the widest.
 *
 * It was the widest while the widest was 5 km, on the rule that narrowing is an
 * opt-in and nobody should land on a narrow radius by not choosing. 5 km still
 * satisfies that. 10 km is four times the area for a list capped at the same 75
 * people, so it is offered rather than imposed.
 */
export const NEARBY_RADIUS_DEFAULT_M = NEARBY_RADIUS_MID_M;

/** A Small Group's own join radius ceiling. Back to following the discovery
 * ceiling (owner decision 2026-08-20): while the two diverged, the group
 * sheets kept this full-width slider and offered hosts ranges the server then
 * refused. The server copy holds the story; if the ceilings ever split again,
 * the group sheets' track must be bounded by THIS constant in the same
 * change. */
export const SMALL_GROUP_RADIUS_MAX_M = NEARBY_RADIUS_MAX_M;

/** The slider's native domain. The midpoint anchor sits exactly at half. */
export const SLIDER_POSITION_MAX = 100;
const MID_POSITION = SLIDER_POSITION_MAX / 2;

const LEFT_SNAP_M = 50;
const RIGHT_SNAP_M = 100;

/** Anything (stored value, server echo, undefined) → a valid radius. */
export function clampNearbyRadiusM(value: unknown): number {
  const n = typeof value === "number" && Number.isFinite(value) ? value : NaN;
  if (Number.isNaN(n)) return NEARBY_RADIUS_DEFAULT_M;
  return Math.min(
    NEARBY_RADIUS_MAX_M,
    Math.max(NEARBY_RADIUS_MIN_M, Math.round(n)),
  );
}

/** Track position (0..100) → snapped metres. */
export function positionToRadiusM(position: number): number {
  const p = Math.min(SLIDER_POSITION_MAX, Math.max(0, position));
  if (p <= MID_POSITION) {
    const raw =
      NEARBY_RADIUS_MIN_M +
      ((NEARBY_RADIUS_MID_M - NEARBY_RADIUS_MIN_M) * p) / MID_POSITION;
    return clampNearbyRadiusM(Math.round(raw / LEFT_SNAP_M) * LEFT_SNAP_M);
  }
  const raw =
    NEARBY_RADIUS_MID_M +
    ((NEARBY_RADIUS_MAX_M - NEARBY_RADIUS_MID_M) * (p - MID_POSITION)) /
      MID_POSITION;
  return clampNearbyRadiusM(Math.round(raw / RIGHT_SNAP_M) * RIGHT_SNAP_M);
}

/** Metres → track position. The inverse of `positionToRadiusM` on its snap
 * points, so opening the slider never moves a previously chosen value. */
export function radiusMToPosition(radiusM: number): number {
  const m = clampNearbyRadiusM(radiusM);
  if (m <= NEARBY_RADIUS_MID_M) {
    return (
      ((m - NEARBY_RADIUS_MIN_M) /
        (NEARBY_RADIUS_MID_M - NEARBY_RADIUS_MIN_M)) *
      MID_POSITION
    );
  }
  return (
    MID_POSITION +
    ((m - NEARBY_RADIUS_MID_M) / (NEARBY_RADIUS_MAX_M - NEARBY_RADIUS_MID_M)) *
      MID_POSITION
  );
}

/**
 * "600 m" / "1.2 km" / "2.5 km" — the number-and-unit part only. Screens wrap
 * it in a translated sentence; the units themselves are SI and locale-stable.
 */
export function formatRadiusM(radiusM: number): string {
  const m = clampNearbyRadiusM(radiusM);
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  const text = Number.isInteger(km) ? String(km) : km.toFixed(1);
  return `${text} km`;
}
